import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { EASE } from "./Reveal";

const KEY = "federasol_intro_seen";
const RIPPLES = [0, 1, 2];

export default function Intro({ onFinish }) {
  const { T } = useLang();
  const reduce = useReducedMotion();
  const [show, setShow] = useState(false);
  const closing = useRef(false);

  const finish = (markSeen) => {
    if (closing.current) return;
    closing.current = true;
    if (markSeen) sessionStorage.setItem(KEY, "1");
    setShow(false);
    document.body.style.overflow = "";
    onFinish();
  };

  useEffect(() => {
    if (reduce || sessionStorage.getItem(KEY) === "1") {
      onFinish();
      return;
    }
    setShow(true);
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => finish(true), 3200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-screen"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-federasol-cream grain overflow-hidden"
          exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
          onClick={() => finish(true)}
          role="dialog"
          aria-label={T.intro.aria}
        >
          {/* Thermal ripples */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {RIPPLES.map((i) => (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 h-[200px] w-[200px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-federasol-rose/35 sm:h-[440px] sm:w-[440px]"
                style={{ animation: `ripple-ring 3.6s cubic-bezier(0.2,0.6,0.3,1) ${i * 1.2}s infinite` }}
              />
            ))}
          </div>

          <div className="relative flex flex-col items-center px-6 text-center">
            <motion.div
              data-testid="intro-logo"
              initial={{ opacity: 0, y: 18, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
            >
              <img
                src="/logo-federasol.png"
                alt="Hotel Federasol"
                width="720"
                height="416"
                className="h-36 w-auto logo-blend sm:h-44 md:h-52"
              />
            </motion.div>

            <motion.span
              className="mt-8 block h-px w-28 bg-federasol-terra/50 sm:w-32"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.1, delay: 1.15, ease: EASE }}
            />

            <motion.p
              data-testid="intro-tagline"
              className="mt-6 max-w-sm font-serif text-2xl italic text-federasol-terra sm:text-3xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.35, ease: EASE }}
            >
              {T.intro.tagline}
            </motion.p>
          </div>

          <motion.button
            data-testid="skip-intro-button"
            onClick={() => finish(true)}
            className="absolute bottom-10 rounded-full border border-federasol-terra/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-federasol-terra transition-colors duration-300 hover:bg-federasol-terra/5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
          >
            {T.intro.ingresar}
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
